import { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { supabase } from '@/lib/supabase';
import { ChevronLeft, Mail, CheckCircle, XCircle } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';

const COOLDOWN_SECONDS = 60;

export default function CheckEmailScreen() {
  const router = useRouter();
  const { theme } = useTheme();
  const { email } = useLocalSearchParams<{ email: string }>();
  const insets = useSafeAreaInsets();
  const [resending, setResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCooldown = useCallback(() => {
    setCooldown(COOLDOWN_SECONDS);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  const handleResendEmail = async () => {
    if (!email) {
      setResendStatus('error');
      setErrorMessage('Email address is missing');
      return;
    }

    setResending(true);
    setResendStatus('idle');
    setErrorMessage('');

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });

      if (error) {
        setResendStatus('error');
        setErrorMessage("Couldn't resend the confirmation email. Please try again in a minute.");
      } else {
        setResendStatus('success');
        startCooldown();
        setTimeout(() => setResendStatus('idle'), 5000);
      }
    } catch {
      setResendStatus('error');
      setErrorMessage("Couldn't resend the confirmation email. Please try again in a minute.");
    } finally {
      setResending(false);
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={[styles.header, { paddingTop: insets.top + 12 }]}>
        <TouchableOpacity
          onPress={() => router.replace('/(auth)/signin')}
          style={styles.backButton}>
          <ChevronLeft size={28} color={theme.colors.text} />
        </TouchableOpacity>
      </View>

      <View style={styles.content}>
        <View style={[styles.iconContainer, { backgroundColor: theme.colors.surface, borderColor: theme.colors.accent }]}>
          <Mail size={64} color={theme.colors.accent} />
        </View>

        <Text style={[styles.title, { color: theme.colors.text }]}>Check Your Email</Text>
        <Text style={[styles.subtitle, { color: theme.colors.textSecondary }]}>
          We&apos;ve sent a confirmation link to:
        </Text>

        <View style={[styles.emailContainer, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
          <Text style={[styles.email, { color: theme.colors.accent }]}>{email || 'your email'}</Text>
        </View>

        <Text style={[styles.instructions, { color: theme.colors.text }]}>
          Click the link in the email to confirm your account and start using DUEL
        </Text>

        {resendStatus === 'success' && (
          <View style={[styles.statusContainer, { backgroundColor: theme.colors.surface }]}>
            <CheckCircle size={20} color={theme.colors.success} />
            <Text style={[styles.successText, { color: theme.colors.success }]}>Confirmation email sent!</Text>
          </View>
        )}

        {resendStatus === 'error' && (
          <View style={[styles.statusContainer, { backgroundColor: theme.colors.surface }]}>
            <XCircle size={20} color={theme.colors.accent} />
            <Text style={[styles.errorText, { color: theme.colors.accent }]}>{errorMessage}</Text>
          </View>
        )}

        <View style={styles.buttonGroup}>
          <TouchableOpacity
            style={[styles.resendButton, { backgroundColor: theme.colors.surface, borderColor: theme.colors.accent }, (resending || cooldown > 0) && styles.buttonDisabled]}
            onPress={handleResendEmail}
            disabled={resending || cooldown > 0}>
            {resending ? (
              <ActivityIndicator color={theme.colors.accent} />
            ) : cooldown > 0 ? (
              <Text style={[styles.resendButtonText, { color: theme.colors.textTertiary }]}>Resend in {cooldown}s</Text>
            ) : (
              <Text style={[styles.resendButtonText, { color: theme.colors.accent }]}>Resend Confirmation Email</Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.signInButton, { backgroundColor: theme.colors.accent }]}
            onPress={() => router.replace('/(auth)/signin')}>
            <Text style={[styles.signInButtonText, { color: theme.colors.accentForeground }]}>Back to Sign In</Text>
          </TouchableOpacity>
        </View>

        <Text style={[styles.helpText, { color: theme.colors.textTertiary }]}>
          Didn&apos;t receive the email?{'\n'}
          <Text style={{ fontWeight: '700' }}>Check your spam folder</Text> — confirmation emails sometimes end up there.
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 20,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    paddingHorizontal: 32,
    paddingTop: 40,
    alignItems: 'center',
  },
  iconContainer: {
    width: 120,
    height: 120,
    borderRadius: 4,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 32,
  },
  title: {
    fontSize: 32,
    fontWeight: '900',
    marginBottom: 12,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 16,
    textAlign: 'center',
  },
  emailContainer: {
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 20,
    paddingVertical: 16,
    marginBottom: 24,
  },
  email: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  instructions: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 32,
    paddingHorizontal: 16,
  },
  statusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 2,
    marginBottom: 24,
  },
  successText: {
    fontSize: 14,
    fontWeight: '600',
  },
  errorText: {
    fontSize: 14,
    fontWeight: '600',
  },
  buttonGroup: {
    width: '100%',
    gap: 16,
  },
  resendButton: {
    borderWidth: 2,
    paddingVertical: 16,
    borderRadius: 2,
    alignItems: 'center',
  },
  resendButtonText: {
    fontSize: 16,
    fontWeight: '700',
  },
  signInButton: {
    paddingVertical: 16,
    borderRadius: 2,
    alignItems: 'center',
  },
  signInButtonText: {
    fontSize: 16,
    fontWeight: '700',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  helpText: {
    fontSize: 12,
    textAlign: 'center',
    marginTop: 24,
    lineHeight: 18,
  },
});
